import { keepPreviousData, staleTimes, useQuery } from '@platform/api-client';
import type { Operation, ResponseBody } from '@platform/openapi';
import { queryKeys, usePageContext } from '../../usePageContext.ts';
import { salesFilterQueryOf } from '../widgets/widget-queries.ts';
import type { WidgetContext } from '../widgets/widget-types.ts';

export type StockValueReport = ResponseBody<Operation<'/admin/dashboard/stock-value', 'get'>>;

export type PriceRange = StockValueReport['items'][number]['salePrice'];

export type StockValueTotals = StockValueReport['totals'] & {
  readonly currency: string;
  readonly unitPurchasePrice: PriceRange | null;
  readonly unitSalePrice: PriceRange | null;
};

const TOTALS_ONLY = 1;

export const useStockValueTotals = (
  context: Pick<WidgetContext, 'api' | 'company' | 'productFilter'>,
  enabled: boolean,
) => {
  const { tenantId } = usePageContext();
  const filters = salesFilterQueryOf(context);
  return useQuery({
    queryKey: [...queryKeys.dashboard(tenantId), 'stockValueTotals', filters],
    queryFn: async (): Promise<StockValueTotals> => {
      const report = await context.api.get('/admin/dashboard/stock-value', {
        query: { ...filters, page_size: TOTALS_ONLY },
      });
      const product = context.productFilter.productId === null ? undefined : report.items[0];
      return {
        ...report.totals,
        currency: report.currency,
        unitPurchasePrice: product?.purchasePrice ?? null,
        unitSalePrice: product?.salePrice ?? null,
      };
    },
    staleTime: staleTimes.dashboard,
    placeholderData: keepPreviousData,
    enabled,
  });
};
